import React, { useMemo, useCallback } from 'react';
import { clearNavigationState } from '../../shared/utils/navigationState';
import useNavigation from '../../shared/utils/useNavigation';
import { getFormFieldValue } from '../../shared/utils/paymentUtil';
import { formatDateTimeZone, formatUsdAmountDisplayCents } from '../../shared/utils/function';
import { TRANSACTION_TYPE, OFFER_TYPE } from '../../shared/constants/common';

// Status Colors
const STATUS_COLOR = {
  GREEN: 'green',
  RED: 'red',
  YELLOW: 'yellow',
  PURPLE: 'purple',
};

// Status Color Class Map
const STATUS_COLOR_CLASS_MAP = {
  [STATUS_COLOR.GREEN]: 'modern-transactions-status-paid',
  [STATUS_COLOR.RED]: 'modern-transactions-status-failed',
  [STATUS_COLOR.YELLOW]: 'modern-transactions-status-pending',
  [STATUS_COLOR.PURPLE]: 'modern-transactions-status-refunded',
};

/**
 * Get transaction status information based on transaction state
 * State values: 0 = not paid, 1 = payment (paid), 2 = pending, 5 = pending
 */
const getTransactionStatusInfo = (state) => {
  switch (state) {
    case 1: { // payment (paid)
      return {
        text: 'Paid',
        color: STATUS_COLOR.GREEN,
        icon: 'fa-check-circle'
      };
    }

    case 2: // pending
    case 5: // pending
      return {
        text: 'Pending',
        color: STATUS_COLOR.YELLOW,
        icon: 'fa-clock'
      };

    case 4: // pending
      return {
        text: 'Cancelled',
        color: STATUS_COLOR.RED,
        icon: 'fa-times-circle'
      };

    case 6 : // Refunded
      return {
        text: 'Refunded',
        color: STATUS_COLOR.PURPLE,
        icon: 'fa-circle-xmark'
      };

    case 0: // not paid
      return {
        text: 'Failed',
        color: STATUS_COLOR.RED,
        icon: 'fa-times-circle'
      };

    default:
      return {
        text: 'Unknown',
        color: 'default',
        icon: 'fa-circle'
      };
  }
};

/**
 * Get status color class name
 */
const getStatusColorClass = (color) => {
  return STATUS_COLOR_CLASS_MAP[color] || 'modern-transactions-status-default';
};

const getAmount = (transaction) => {
  // Map transaction types to their payment form paths
  const paymentFormPathMap = {
    [TRANSACTION_TYPE.TIP]: transaction?.type_tip?.payment?.paymentForm,
    [TRANSACTION_TYPE.SUBSCRIPTION]: transaction?.type_subscription?.payment?.paymentForm,
    [TRANSACTION_TYPE.PURCHASE_CONTENT]: transaction?.type_purchase_content?.payment?.paymentForm,
    [TRANSACTION_TYPE.WALLET_CREDIT_PURCHASE]: transaction?.type_wallet_credit_purchase?.payment?.paymentForm,
  };

  const paymentForm = paymentFormPathMap[transaction.type];
  const initialPrice = paymentForm ? getFormFieldValue(paymentForm, 'initialPrice') : undefined;

  const subtotal = Number(transaction.subtotal) || 0;
  const ccbillFee = Number(transaction.ccbillTransactionFee) || 0;
  const vatAmount = Number(transaction.vatAmount) || 0;
  let amount = (subtotal + ccbillFee + vatAmount).toFixed(2);
  return initialPrice || amount;
};

/**
 * Get transaction type information
 */
const getTransactionTypeInfo = (transaction) => {
  if (!transaction) {
    return { text: 'Unknown' };
  }

  const offerType = transaction?.applied_promotion?.offer_type;
  if (offerType === OFFER_TYPE.FREE_TRIAL) {
    return { text: 'Trial Subscription' };
  }

  let text = '';
  switch (transaction.type) {
    case TRANSACTION_TYPE.SUBSCRIPTION:
      text = 'Monthly Subscription';
      break;

    case TRANSACTION_TYPE.TIP:
      text = 'Tip';
      break;

    case TRANSACTION_TYPE.MESSAGES_TIP:
      text = 'Message Tip';
      break;

    case TRANSACTION_TYPE.PURCHASE_CONTENT:
      text = 'Content Purchase';
      break;

    case TRANSACTION_TYPE.MESSAGE_PURCHASE_CONTENT:
      text = 'Message Content Purchase';
      break;

    case TRANSACTION_TYPE.WALLET_CREDIT_PURCHASE:
      text = 'Call Credits';
      break;

    case TRANSACTION_TYPE.VOICE_CALL:
      text = 'Voice Call';
      break;

    case TRANSACTION_TYPE.VIDEO_CALL:
      text = 'Video Call';
      break;

    case TRANSACTION_TYPE.CALL_TIP:
      text = 'Call Tip';
      break;
      
    default:
      text = transaction.type
        ? transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1).replace('_', ' ')
        : 'Unknown';
  }

  return { text };
};

/**
 * Transaction Item Component
 * Displays a single transaction with status, type, amount, and date information
 */
const TransactionItem = ({
  transaction
}) => {
  const navigate = useNavigation();
  const purchaseId = transaction.purchaseId || 'N/A';
  const date = useMemo(
    () => formatDateTimeZone(transaction.created_at, 'Month DD, YYYY') || 'Unknown',
    [transaction.created_at]
  );
  const creatorUsername = transaction.creatorUsername || '';
  const statusInfo = useMemo(() => getTransactionStatusInfo(transaction.state), [
    transaction.state,
  ]);
  const amount = useMemo(() => {
    const raw = getAmount(transaction);
    const num = Number(raw);
    return Number.isFinite(num) ? formatUsdAmountDisplayCents(num) : raw;
  }, [transaction]);
  const typeInfo = useMemo(() => getTransactionTypeInfo(transaction), [transaction]);

  const onCreatorClick = useCallback(
    (link) => {
      if (link) {
        clearNavigationState();
        navigate.to(link);
      }
    }, []);

  return (
    <div className="modern-transactions-item">
      <div className="modern-transactions-item-header">
        <div className="modern-transactions-item-info">
          <span className="modern-transactions-purchase-id">
            Purchase ID: {purchaseId}
          </span>
          <p className="modern-transactions-date">
            {date}
          </p>

          {creatorUsername && (
            <p
              className={`modern-transactions-creator ${creatorUsername ? 'link' : 'no-link'}`}
              onClick={() => onCreatorClick(creatorUsername)}
            >
              {`@${creatorUsername}`}
            </p>
          )}
        </div>
        <span className="modern-transactions-amount">
          {parseFloat(amount) === 0 ? 'Free' : `$${amount} USD`}
        </span>
      </div>
      <div className="modern-transactions-item-footer">
        <span className={`modern-transactions-status ${getStatusColorClass(statusInfo.color)}`}>
          <i className={`fa-solid ${statusInfo.icon} modern-transactions-status-icon`}></i>
          {statusInfo.text}
        </span>

        <span className="modern-transactions-type">
          {typeInfo.text}
        </span>
      </div>
    </div>
  );
};

export default TransactionItem;

